import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiActivity,
  FiAlertCircle,
  FiExternalLink,
  FiMessageCircle,
  FiRefreshCw,
  FiSend,
} from "react-icons/fi";
import {
  fetchLeadAnalytics,
  getErrorMessage,
} from "../services/facebookCommentService";

function formatNumber(value) {
  return Number(value || 0).toLocaleString("vi-VN");
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString("vi-VN", {
    minimumFractionDigits: value % 1 ? 1 : 0,
    maximumFractionDigits: 1,
  })}%`;
}

function formatRelativeTime(timestamp) {
  if (!timestamp) {
    return "Vừa xong";
  }

  const diffMinutes = Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes} phút trước`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} giờ trước`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} ngày trước`;
}

const LeadConversionDashboard = () => {
  const [userId, setUserId] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const currentUserId = localStorage.getItem("userId");
    setUserId(currentUserId || "");
  }, []);

  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    let cancelled = false;

    const loadAnalytics = async ({ silent = false } = {}) => {
      if (!silent) {
        setRefreshing(true);
      }

      try {
        const data = await fetchLeadAnalytics(userId, 30);
        if (!cancelled) {
          setAnalytics(data);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Không thể tải dashboard lead"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    loadAnalytics({ silent: true });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
          Đang tải Lead Conversion Dashboard...
        </div>
      </div>
    );
  }

  const overview = analytics?.overview || {};
  const today = analytics?.today || {};
  const byPage = analytics?.byPage || [];
  const pendingHotLeads = analytics?.pendingHotLeads || [];
  const pendingHotLeadCount = Number(overview.pendingHotLeadCount || 0);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <section className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 text-white shadow-[0_24px_80px_-36px_rgba(15,23,42,0.9)]">
        <div className="border-b border-white/10 px-6 py-5 md:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-sm font-medium text-cyan-100">
                <FiActivity />
                Lead Conversion Engine
              </div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-4xl">
                Tự động ra đơn từ comment
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 md:text-base">
                Không chỉ phản hồi bình luận. Engine này giúp bạn nhìn ra lead,
                đẩy team sale vào đúng thời điểm và cho thấy page nào đang tạo
                cơ hội chốt đơn tốt nhất.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={async () => {
                  setRefreshing(true);
                  try {
                    const data = await fetchLeadAnalytics(userId, 30);
                    setAnalytics(data);
                  } catch (error) {
                    toast.error(
                      getErrorMessage(error, "Không thể làm mới dashboard lead")
                    );
                  } finally {
                    setRefreshing(false);
                  }
                }}
                disabled={!userId || refreshing}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/15 disabled:opacity-60"
              >
                <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
                Làm mới
              </button>
              <Link
                to="/facebook-comment-automation"
                className="inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
              >
                Mở cài đặt engine
              </Link>
            </div>
          </div>
        </div>

        <div className="grid gap-4 px-6 py-6 md:grid-cols-4 md:px-8">
          {[
            {
              icon: <FiMessageCircle />,
              label: "Comment nhận",
              value: formatNumber(overview.totalReceived),
              meta: `${formatNumber(today.received)} hôm nay`,
            },
            {
              icon: <FiSend />,
              label: "Engine đã xử lý",
              value: formatNumber(
                Number(overview.totalSent || 0) +
                  Number(overview.totalManualFollowUp || 0)
              ),
              meta: `${formatPercent(overview.handledRate)} đã chạm tới`,
            },
            {
              icon: <FiActivity />,
              label: "Lead surfaced",
              value: formatNumber(overview.totalLeads),
              meta: `${formatPercent(overview.leadRate)} từ comment`,
            },
            {
              icon: <FiAlertCircle />,
              label: "Hot lead cần chạm ngay",
              value: formatNumber(pendingHotLeadCount),
              meta: `${formatNumber(today.hotLeads)} lead nóng hôm nay`,
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur"
            >
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-cyan-100">
                {item.icon}
              </div>
              <p className="text-sm text-slate-300">{item.label}</p>
              <p className="mt-2 text-3xl font-semibold text-white">
                {item.value}
              </p>
              <p className="mt-1 text-xs text-slate-400">{item.meta}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Funnel lead 30 ngày
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Comment vào, engine xử lý, lead được surface và case cần sale
                follow-up ngay.
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            {[
              {
                label: "Comment nhận",
                value: overview.totalReceived,
                color: "bg-slate-900",
                ratio: 100,
              },
              {
                label: "Engine đã xử lý",
                value:
                  Number(overview.totalSent || 0) +
                  Number(overview.totalManualFollowUp || 0),
                color: "bg-cyan-600",
                ratio: overview.handledRate,
              },
              {
                label: "Lead surfaced",
                value: overview.totalLeads,
                color: "bg-amber-500",
                ratio: overview.leadRate,
              },
              {
                label: "Lead nóng",
                value: overview.totalHotLeads,
                color: "bg-rose-500",
                ratio: overview.hotLeadRate,
              },
            ].map((item) => (
              <div key={item.label}>
                <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium text-slate-700">{item.label}</span>
                  <span className="text-slate-500">
                    {formatNumber(item.value)}
                    {item.label !== "Comment nhận" ? ` • ${formatPercent(item.ratio)}` : ""}
                  </span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${item.color}`}
                    style={{ width: `${Math.min(100, Number(item.ratio || 0) || 0)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Hot lead cần sale xử lý
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Các case có tín hiệu mua mạnh và đang cần hành động ngay.
              </p>
            </div>
            <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
              {formatNumber(pendingHotLeadCount)} lead
            </span>
          </div>

          <div className="mt-5 space-y-3">
            {pendingHotLeadCount > pendingHotLeads.length && (
              <p className="text-xs text-slate-500">
                Đang hiển thị {formatNumber(pendingHotLeads.length)} / {formatNumber(pendingHotLeadCount)} hot lead gần nhất.
              </p>
            )}
            {pendingHotLeads.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Chưa có hot lead nào đang chờ sale follow-up.
              </div>
            )}

            {pendingHotLeads.map((lead) => {
              const isManual = lead.status !== "sent";
              const activityAt = lead.lastInboundAt || lead.updatedAt;

              return (
                <div
                  key={`${lead.pageId}-${lead.threadKey}`}
                  className="rounded-2xl border border-slate-200 p-4 transition hover:border-cyan-200 hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-900">
                          {lead.customerName || "Khách hàng"}
                        </p>
                        <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-600">
                          Lead nóng
                        </span>
                        <span
                          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            isManual
                              ? "bg-amber-50 text-amber-700"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          {isManual ? "Cần sale xử lý" : "AI đã reply"}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {lead.pageName || lead.pageId} • {formatRelativeTime(activityAt)}
                      </p>
                    </div>

                    {lead.commentLink ? (
                      <a
                        href={lead.commentLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        <FiExternalLink />
                        Mở Facebook
                      </a>
                    ) : null}
                  </div>

                  <p className="mt-3 text-sm leading-6 text-slate-700">
                    {lead.latestMessage || "(không có nội dung)"}
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1">
                      Intent: {lead.intent || "lead"}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1">
                      Status: {lead.followUpMode || lead.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Page tạo cơ hội tốt nhất
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              So sánh luồng comment, khả năng surfacing lead và lượng hot lead
              theo từng fanpage.
            </p>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="pb-3 pr-4 font-medium">Page</th>
                <th className="pb-3 pr-4 font-medium">Comment nhận</th>
                <th className="pb-3 pr-4 font-medium">Engine xử lý</th>
                <th className="pb-3 pr-4 font-medium">Lead surfaced</th>
                <th className="pb-3 pr-4 font-medium">Lead nóng</th>
                <th className="pb-3 font-medium">Lead rate</th>
              </tr>
            </thead>
            <tbody>
              {byPage.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center text-sm text-slate-500"
                  >
                    Chưa có dữ liệu theo page trong khoảng thời gian đã chọn.
                  </td>
                </tr>
              )}
              {byPage.map((page) => (
                <tr key={page.pageId} className="border-b border-slate-100">
                  <td className="py-4 pr-4">
                    <div>
                      <p className="font-medium text-slate-900">{page.pageName}</p>
                      <p className="text-xs text-slate-500">{page.pageId}</p>
                    </div>
                  </td>
                  <td className="py-4 pr-4 text-slate-700">
                    {formatNumber(page.received)}
                  </td>
                  <td className="py-4 pr-4 text-slate-700">
                    {formatNumber(
                      Number(page.sent || 0) + Number(page.manualFollowUp || 0)
                    )}
                  </td>
                  <td className="py-4 pr-4 text-slate-700">
                    {formatNumber(
                      Number(page.warmLeads || 0) + Number(page.hotLeads || 0)
                    )}
                  </td>
                  <td className="py-4 pr-4">
                    <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600">
                      {formatNumber(page.hotLeads)}
                    </span>
                  </td>
                  <td className="py-4 font-medium text-cyan-700">
                    {formatPercent(page.leadRate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default LeadConversionDashboard;
